import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Button,
  DataTable,
  Dialog,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import type { ClaimBatch, PanelClaim } from '@/features/admin-v2/api';
import { formatRM, parseRM } from '@/features/billing/money';
import { api, isApiError } from '@/lib/api';
import { dayLabel } from '@/pages/ops/register-format';
import local from './finance.module.css';

/** GET /finance/invoices/:id (the part this window needs). */
interface InvoiceDetail {
  id: string;
  number: string;
  status: string;
  outstandingSen?: number;
  claims: PanelClaim[];
  creditNotes?: {
    id: string;
    number: string;
    claimId: string;
    amountSen: number;
    reason: string;
    createdAt: string;
  }[];
}

/** Claims the panel still owes on, and how much. */
const owedOn = (c: PanelClaim) =>
  ['paid', 'written_off', 'billed_patient'].includes(c.status)
    ? 0
    : Math.max(0, c.amountSen - c.paidSen - c.writtenOffSen);

/**
 * RL-1770 (gap #43): an issued invoice never changes, so a correction is a
 * credit note on one of its claims, at most what the panel still owes on it.
 */
export function CreditNoteDialog({
  invoice,
  onClose,
}: {
  invoice: ClaimBatch;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const detail = useQuery({
    queryKey: ['finance', 'invoice', invoice.id],
    queryFn: ({ signal }) =>
      api
        .get<InvoiceDetail>(`/finance/invoices/${invoice.id}`, { signal })
        .then((r) => r.data),
  });
  const [claimId, setClaimId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const issue = useMutation({
    mutationFn: (body: object) =>
      api.post(`/finance/invoices/${invoice.id}/credit-notes`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['finance'] }),
  });
  const owed = (detail.data?.claims ?? []).filter((c) => owedOn(c) > 0);
  const picked = owed.find((c) => c.id === claimId);
  const submit = () => {
    const sen = parseRM(amount);
    if (!picked)
      return toast.show({ tone: 'error', title: 'Choose the claim' });
    if (!sen || sen > owedOn(picked))
      return toast.show({
        tone: 'error',
        title: `Enter an amount up to ${formatRM(owedOn(picked))}`,
      });
    if (reason.trim().length < 3)
      return toast.show({ tone: 'error', title: 'Give the reason' });
    issue
      .mutateAsync({ claimId, amountSen: sen, reason: reason.trim() })
      .then(() => {
        toast.show({ tone: 'success', title: 'Credit note issued' });
        onClose();
      })
      .catch((err: unknown) =>
        toast.show({
          tone: 'error',
          title: 'Not issued',
          body: isApiError(err) ? err.message : undefined,
          error: err,
        }),
      );
  };
  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={`Credit note · ${invoice.number}`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={issue.isPending} onClick={submit}>
            Issue credit note
          </Button>
        </>
      }
    >
      <div className={local.form}>
        <p className={local.note}>
          An issued invoice does not change. A credit note lowers what the panel
          owes on one of its claims.
        </p>
        <Field label="Claim">
          <Select
            value={claimId}
            onChange={(e) => setClaimId(e.target.value)}
            placeholder={detail.isLoading ? 'Loading…' : 'Choose a claim'}
            options={owed.map((c) => ({
              value: c.id,
              label: `${c.bill.number} · ${c.patient.fullName} · owes ${formatRM(owedOn(c))}`,
            }))}
          />
        </Field>
        <Field
          label="Amount (RM)"
          hint={picked ? `Up to ${formatRM(owedOn(picked))}` : undefined}
        >
          <Input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field label="Reason">
          <Input
            value={reason}
            maxLength={300}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        {!!detail.data?.creditNotes?.length && (
          <DataTable
            caption="Credit notes on this invoice"
            rows={detail.data.creditNotes}
            rowKey={(n) => n.id}
            columns={[
              {
                key: 'n',
                header: 'Credit note',
                render: (n) => (
                  <>
                    <span className="mono">{n.number}</span>
                    <p className={local.note}>{dayLabel(n.createdAt)}</p>
                  </>
                ),
              },
              { key: 'r', header: 'Reason', render: (n) => n.reason },
              {
                key: 'a',
                header: 'Amount',
                align: 'right',
                render: (n) => formatRM(n.amountSen),
              },
            ]}
          />
        )}
      </div>
    </Dialog>
  );
}
